import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Loader2, Check, X, ChevronDown, Sparkles, Image as ImageIcon, Palette, Type } from 'lucide-react';
import { api } from '../../lib/api';
import { useBrandIdentity } from '../../contexts/BrandIdentityContext';
import ImageLibrarySection from './ImageLibrarySection';

const PRESET_COLORS = [
  { name: 'Amarelo', hex: '#F59E0B' },
  { name: 'Laranja', hex: '#F97316' },
  { name: 'Vermelho', hex: '#EF4444' },
  { name: 'Rosa', hex: '#EC4899' },
  { name: 'Roxo', hex: '#8B5CF6' },
  { name: 'Azul', hex: '#3B82F6' },
  { name: 'Ciano', hex: '#06B6D4' },
  { name: 'Verde', hex: '#10B981' },
  { name: 'Preto', hex: '#000000' },
  { name: 'Branco', hex: '#FFFFFF' },
  { name: 'Cinza', hex: '#6B7280' },
];

// Fontes disponíveis com seus nomes de exibição e valores para o agente
const AVAILABLE_FONTS = [
  { display: 'Montserrat', value: 'montserrat', variants: ['montserrat-bold', 'montserrat-black', 'montserrat-light'] },
  { display: 'Montserrat Bold', value: 'montserrat bold', variants: ['montserrat-bold'] },
  { display: 'Montserrat Black', value: 'montserrat black', variants: ['montserrat-black'] },
  { display: 'Montserrat Light', value: 'montserrat light', variants: ['montserrat-light'] },
  { display: 'Inter', value: 'inter', variants: ['Inter-Bold', 'Inter-Black'] },
  { display: 'Inter Bold', value: 'inter bold', variants: ['Inter-Bold'] },
  { display: 'Inter Black', value: 'inter black', variants: ['Inter-Black'] },
  { display: 'Poppins', value: 'poppins', variants: ['poppins-bold', 'poppins-black'] },
  { display: 'Poppins Bold', value: 'poppins bold', variants: ['poppins-bold'] },
  { display: 'Poppins Black', value: 'poppins black', variants: ['poppins-black'] },
  { display: 'Roboto', value: 'roboto', variants: ['roboto-bold'] },
  { display: 'Roboto Bold', value: 'roboto bold', variants: ['roboto-bold'] },
  { display: 'Open Sans', value: 'open sans', variants: ['opensans-bold'] },
  { display: 'Open Sans Bold', value: 'open sans bold', variants: ['opensans-bold'] },
  { display: 'Lato', value: 'lato', variants: ['lato-bold'] },
  { display: 'Lato Bold', value: 'lato bold', variants: ['lato-bold'] },
  { display: 'Raleway', value: 'raleway', variants: ['raleway-bold'] },
  { display: 'Raleway Bold', value: 'raleway bold', variants: ['raleway-bold'] },
  { display: 'Nunito', value: 'nunito', variants: ['nunito-bold'] },
  { display: 'Oswald', value: 'oswald', variants: ['oswald-bold'] },
  { display: 'Oswald Bold', value: 'oswald bold', variants: ['oswald-bold'] },
  { display: 'Playfair Display', value: 'playfair display', variants: ['playfairdisplay-bold'] },
  { display: 'Bangers', value: 'bangers', variants: ['bangers-regular'] },
  { display: 'Bungee', value: 'bungee', variants: ['bungee-regular'] },
  { display: 'Lilita One', value: 'lilita one', variants: ['lilitaone'] },
  { display: 'Lobster', value: 'lobster', variants: ['lobster-regular'] },
  { display: 'Pacifico', value: 'pacifico', variants: ['pacifico-regular'] },
  { display: 'Barlow Condensed', value: 'barlow condensed', variants: ['barlowcondensed-bold'] },
  { display: 'Space Mono', value: 'space mono', variants: ['spacemono-bold'] },
  { display: 'IBM Plex Sans', value: 'ibm plex', variants: ['ibmplexsans-bold'] },
  { display: 'Source Sans Pro', value: 'source sans', variants: ['sourcesanspro-bold'] },
];

// Categorias de fontes para melhor organização
const FONT_CATEGORIES = {
  'Sans-Serif Modernas': ['inter', 'inter bold', 'inter black', 'poppins', 'poppins bold', 'poppins black', 'montserrat', 'montserrat bold', 'montserrat black', 'montserrat light'],
  'Sans-Serif Clássicas': ['roboto', 'roboto bold', 'open sans', 'open sans bold', 'lato', 'lato bold', 'source sans', 'ibm plex'],
  'Display & Títulos': ['oswald', 'oswald bold', 'raleway', 'raleway bold', 'nunito', 'barlow condensed'],
  'Decorativas': ['bangers', 'bungee', 'lilita one', 'lobster', 'pacifico'],
  'Serif & Elegantes': ['playfair display'],
  'Monoespaçadas': ['space mono'],
};

const BrandIdentitySummary = () => {
  const { updateBrandIdentity, reloadBrandIdentity } = useBrandIdentity();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [colors, setColors] = useState({ primary: '#F59E0B', secondary: '#000000' });
  const [fonts, setFonts] = useState({ primary: 'inter', secondary: 'poppins' });
  const [aboutBusiness, setAboutBusiness] = useState('');
  const [imageLibrary, setImageLibrary] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showPrimaryPresets, setShowPrimaryPresets] = useState(false);
  const [showSecondaryPresets, setShowSecondaryPresets] = useState(false);
  const [showPrimaryFonts, setShowPrimaryFonts] = useState(false);
  const [showSecondaryFonts, setShowSecondaryFonts] = useState(false);

  useEffect(() => {
    loadBrandIdentity();
  }, []);

  const colorNameToHex = (colorName) => {
    if (!colorName) return '#F59E0B';
    if (colorName.startsWith('#')) return colorName;
    const found = PRESET_COLORS.find(c => c.name.toLowerCase() === colorName.toLowerCase());
    return found ? found.hex : '#F59E0B';
  };

  const getFontDisplay = (fontValue) => {
    if (!fontValue) return 'Inter';
    const found = AVAILABLE_FONTS.find(f => f.value.toLowerCase() === fontValue.toLowerCase());
    return found ? found.display : fontValue;
  };

  const loadBrandIdentity = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/user/brand-identity');
      const brandData = response.data?.data || response.data || {};
      
      if (brandData.logo_url) {
        setLogoUrl(brandData.logo_url);
        setLogoPreview(brandData.logo_url);
      }
      
      if (brandData.colors) {
        setColors({
          primary: colorNameToHex(brandData.colors.primary),
          secondary: colorNameToHex(brandData.colors.secondary)
        });
      }
      
      if (brandData.fonts) {
        setFonts({
          primary: brandData.fonts.primary || 'inter',
          secondary: brandData.fonts.secondary || 'poppins'
        });
      }
      
      if (brandData.about_business) {
        setAboutBusiness(brandData.about_business);
      }

      if (brandData.image_library) {
        setImageLibrary(brandData.image_library);
      }
    } catch (err) {
      console.error('Erro ao carregar identidade visual:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const autoSave = useCallback(async (data) => {
    setSaveStatus('saving');
    try {
      await api.put('/api/user/brand-identity', data);
      updateBrandIdentity(data);
      window.dispatchEvent(new Event('brandIdentityUpdated'));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
      setTimeout(() => reloadBrandIdentity(), 500);
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  }, [updateBrandIdentity, reloadBrandIdentity]);

  useEffect(() => {
    if (isLoading) return;
    
    const timeoutId = setTimeout(() => {
      autoSave({
        logo_url: logoUrl || '',
        colors: { primary: colors.primary, secondary: colors.secondary },
        fonts: { primary: fonts.primary, secondary: fonts.secondary },
        about_business: aboutBusiness || ''
      });
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [colors.primary, colors.secondary, fonts.primary, fonts.secondary, aboutBusiness]);

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) return;

    setIsUploading(true);

    try {
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);

      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await api.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const responseData = uploadResponse.data;
      const uploadedUrl = responseData?.data?.file_url || responseData?.file_url;
      
      if (uploadedUrl) {
        setLogoUrl(uploadedUrl);
        setLogoPreview(uploadedUrl);
        await api.put('/api/user/brand-identity', { logo_url: uploadedUrl });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2000);
      }
    } catch (err) {
      console.error('Erro ao fazer upload:', err);
      setLogoPreview(logoUrl);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setIsUploading(false);
    }
  };

  const removeLogo = async () => {
    const previousUrl = logoUrl;
    setLogoUrl(null);
    setLogoPreview(null);
    
    try {
      await api.put('/api/user/brand-identity', { logo_url: '' });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      console.error('Erro ao remover logo:', err);
      setLogoUrl(previousUrl);
      setLogoPreview(previousUrl);
    }
  };

  const handleColorChange = (type, value) => {
    let hex = value;
    if (!hex.startsWith('#')) {
      hex = '#' + hex;
    }
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex)) {
      setColors(prev => ({ ...prev, [type]: hex }));
    }
  };

  const handleFontChange = (type, value) => {
    setFonts(prev => ({ ...prev, [type]: value }));
    if (type === 'primary') {
      setShowPrimaryFonts(false);
    } else {
      setShowSecondaryFonts(false);
    }
  };

  const handleImageLibraryUpdate = (updatedImages) => {
    setImageLibrary(updatedImages);
  };

  // Componente de seletor de fonte
  const FontSelector = ({ type, value, isOpen, onToggle, onSelect }) => {
    const currentFont = AVAILABLE_FONTS.find(f => f.value === value) || AVAILABLE_FONTS[0];
    
    return (
      <div className="relative">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
              <Type className="w-4 h-4 text-gray-600" />
            </div>
            <span 
              className="font-medium text-gray-900"
              style={{ fontFamily: currentFont.display.split(' ')[0] }}
            >
              {currentFont.display}
            </span>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-80 overflow-y-auto">
            {Object.entries(FONT_CATEGORIES).map(([category, fontValues]) => (
              <div key={category}>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {category}
                  </span>
                </div>
                {fontValues.map(fontValue => {
                  const font = AVAILABLE_FONTS.find(f => f.value === fontValue);
                  if (!font) return null;
                  
                  return (
                    <button
                      key={font.value}
                      onClick={() => onSelect(type, font.value)}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                        value === font.value ? 'bg-amber-50 border-l-2 border-amber-500' : ''
                      }`}
                    >
                      <span 
                        className="text-gray-900"
                        style={{ fontFamily: font.display.split(' ')[0] }}
                      >
                        {font.display}
                      </span>
                      {value === font.value && (
                        <Check className="w-4 h-4 text-amber-600 ml-auto" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-2xl p-4">
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
        </div>
      </div>
    );
  }

  // Modo compacto
  if (!isExpanded) {
    return (
      <div className="bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-2xl p-4 hover:shadow-md transition-all">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Logo */}
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <Sparkles className="w-5 h-5 text-gray-300" />
              )}
            </div>
            
            {/* Cores */}
            <div className="flex items-center gap-1.5">
              <div 
                className="w-8 h-8 rounded-lg border-2 border-white shadow-md ring-1 ring-gray-200/50"
                style={{ backgroundColor: colors.primary }}
                title={`Primária: ${colors.primary}`}
              />
              <div 
                className="w-8 h-8 rounded-lg border-2 border-white shadow-md ring-1 ring-gray-200/50 -ml-2"
                style={{ backgroundColor: colors.secondary }}
                title={`Secundária: ${colors.secondary}`}
              />
            </div>

            {/* Fontes */}
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 border border-gray-200/50 rounded-lg">
              <Type className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs font-medium text-gray-600">
                {getFontDisplay(fonts.primary)}
              </span>
            </div>

            {/* Indicador de imagens */}
            {imageLibrary.length > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200/50 rounded-lg">
                <ImageIcon className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-xs font-semibold text-amber-700">{imageLibrary.length}</span>
              </div>
            )}

            {/* Descrição */}
            {aboutBusiness && (
              <p className="text-sm text-gray-500 max-w-xs truncate hidden lg:block">
                {aboutBusiness}
              </p>
            )}
          </div>

          {/* Botão Editar */}
          <button
            onClick={() => setIsExpanded(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-xl transition-all hover:shadow-sm"
          >
            Personalizar
          </button>
        </div>
      </div>
    );
  }

  // Modo expandido
  return (
    <div className="bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-2xl p-5 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
            <Palette className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Identidade Visual</h3>
            <p className="text-xs text-gray-500">Personalize sua marca</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Status de salvamento */}
          {saveStatus && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              saveStatus === 'saving' ? 'bg-amber-50 text-amber-700' :
              saveStatus === 'saved' ? 'bg-green-50 text-green-700' :
              'bg-red-50 text-red-700'
            }`}>
              {saveStatus === 'saving' && <Loader2 className="w-3 h-3 animate-spin" />}
              {saveStatus === 'saved' && <Check className="w-3 h-3" />}
              {saveStatus === 'error' && <X className="w-3 h-3" />}
              <span>
                {saveStatus === 'saving' ? 'Salvando...' :
                 saveStatus === 'saved' ? 'Salvo' : 'Erro'}
              </span>
            </div>
          )}
          
          <button
            onClick={() => setIsExpanded(false)}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="space-y-5">
        {/* Logo Upload */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Logo
          </label>
          <div className="flex items-center gap-3">
            <div className="relative w-16 h-16 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden group">
              {logoPreview ? (
                <>
                  <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                  <button
                    onClick={removeLogo}
                    className="absolute inset-0 bg-black/50 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <Sparkles className="w-6 h-6 text-gray-300" />
              )}
            </div>
            <label className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-xl cursor-pointer transition-all">
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={isUploading} />
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              <span>{logoPreview ? 'Trocar' : 'Upload'}</span>
            </label>
          </div>
        </div>

        {/* Cores */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Cor Primária */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Cor Primária
            </label>
            <div className="relative">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={colors.primary}
                  onChange={(e) => setColors(prev => ({ ...prev, primary: e.target.value }))}
                  className="w-11 h-11 rounded-xl border-2 border-gray-200 cursor-pointer p-1 bg-white"
                />
                <input
                  type="text"
                  value={colors.primary.toUpperCase()}
                  onChange={(e) => handleColorChange('primary', e.target.value)}
                  placeholder="#000000"
                  className="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-100 focus:border-amber-400 font-mono bg-white"
                />
                <button
                  onClick={() => {
                    setShowPrimaryPresets(!showPrimaryPresets);
                    setShowSecondaryPresets(false);
                    setShowPrimaryFonts(false);
                    setShowSecondaryFonts(false);
                  }}
                  className="p-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors bg-white"
                >
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showPrimaryPresets ? 'rotate-180' : ''}`} />
                </button>
              </div>
              
              {showPrimaryPresets && (
                <div className="absolute top-full left-0 right-0 mt-2 p-3 bg-white border border-gray-200 rounded-xl shadow-xl z-10">
                  <div className="grid grid-cols-6 gap-2">
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={color.hex}
                        onClick={() => {
                          setColors(prev => ({ ...prev, primary: color.hex }));
                          setShowPrimaryPresets(false);
                        }}
                        className={`w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${
                          colors.primary === color.hex ? 'border-gray-900 ring-2 ring-amber-200' : 'border-gray-200'
                        }`}
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Cor Secundária */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Cor Secundária
            </label>
            <div className="relative">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={colors.secondary}
                  onChange={(e) => setColors(prev => ({ ...prev, secondary: e.target.value }))}
                  className="w-11 h-11 rounded-xl border-2 border-gray-200 cursor-pointer p-1 bg-white"
                />
                <input
                  type="text"
                  value={colors.secondary.toUpperCase()}
                  onChange={(e) => handleColorChange('secondary', e.target.value)}
                  placeholder="#000000"
                  className="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-100 focus:border-amber-400 font-mono bg-white"
                />
                <button
                  onClick={() => {
                    setShowSecondaryPresets(!showSecondaryPresets);
                    setShowPrimaryPresets(false);
                    setShowPrimaryFonts(false);
                    setShowSecondaryFonts(false);
                  }}
                  className="p-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors bg-white"
                >
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showSecondaryPresets ? 'rotate-180' : ''}`} />
                </button>
              </div>
              
              {showSecondaryPresets && (
                <div className="absolute top-full left-0 right-0 mt-2 p-3 bg-white border border-gray-200 rounded-xl shadow-xl z-10">
                  <div className="grid grid-cols-6 gap-2">
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={color.hex}
                        onClick={() => {
                          setColors(prev => ({ ...prev, secondary: color.hex }));
                          setShowSecondaryPresets(false);
                        }}
                        className={`w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${
                          colors.secondary === color.hex ? 'border-gray-900 ring-2 ring-amber-200' : 'border-gray-200'
                        }`}
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fontes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Fonte Primária */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Fonte Primária
            </label>
            <FontSelector
              type="primary"
              value={fonts.primary}
              isOpen={showPrimaryFonts}
              onToggle={() => {
                setShowPrimaryFonts(!showPrimaryFonts);
                setShowSecondaryFonts(false);
                setShowPrimaryPresets(false);
                setShowSecondaryPresets(false);
              }}
              onSelect={handleFontChange}
            />
          </div>

          {/* Fonte Secundária */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Fonte Secundária
            </label>
            <FontSelector
              type="secondary"
              value={fonts.secondary}
              isOpen={showSecondaryFonts}
              onToggle={() => {
                setShowSecondaryFonts(!showSecondaryFonts);
                setShowPrimaryFonts(false);
                setShowPrimaryPresets(false);
                setShowSecondaryPresets(false);
              }}
              onSelect={handleFontChange}
            />
          </div>
        </div>

        {/* Preview das Fontes */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Preview das Fontes
          </p>
          <div className="space-y-2">
            <p 
              className="text-xl text-gray-900"
              style={{ fontFamily: getFontDisplay(fonts.primary).split(' ')[0] }}
            >
              {getFontDisplay(fonts.primary)} - Título Principal
            </p>
            <p 
              className="text-base text-gray-700"
              style={{ fontFamily: getFontDisplay(fonts.secondary).split(' ')[0] }}
            >
              {getFontDisplay(fonts.secondary)} - Texto secundário e descrições
            </p>
          </div>
        </div>

        {/* Sobre o negócio */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Sobre o negócio
          </label>
          <textarea
            value={aboutBusiness}
            onChange={(e) => setAboutBusiness(e.target.value.slice(0, 200))}
            placeholder="Descreva seu negócio em poucas palavras..."
            rows={2}
            className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-100 focus:border-amber-400 resize-none transition-all bg-white"
          />
          <p className="text-xs text-gray-400 mt-1.5 text-right">{aboutBusiness.length}/200</p>
        </div>

        {/* Biblioteca de Imagens */}
        <ImageLibrarySection 
          images={imageLibrary}
          onUpdate={handleImageLibraryUpdate}
        />
      </div>
    </div>
  );
};

export default BrandIdentitySummary;